import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useCallback, useState, useEffect } from 'react';
import useLadyService from '@/services/LadyService';
import {
	getFilterEditObj,
	getFilterQuery,
	getFilterState,
	getProfilesOnMapCount,
	setFilterQuery,
	setProfilesOnMapCount,
	setSearchNick,
	syncronomousToggleSomeFilter,
	syncronomousUpdateFilter,
	syncronomousUpdateRangesFilter,
	updateFilter,
	updateCurrentRanges,
} from '@/stores/slices/filterSlice';
import { useFilterComputed } from './filterlogicHooks/useFilterComputed';
import { useUrlParams } from './filterlogicHooks/useUrlParams';
import { useProfilesFetching } from './filterlogicHooks/useProfilesFetching';
import { useFilterStateSync } from './filterlogicHooks/useFilterStateSync';
import {
	getUserCurrentCity,
	getUserCurrentDistrict,
	getCountriesData,
	setCurrentCity,
	setLinksCurrentCity,
} from '@/stores/slices/userSlice';
import { getValidDistrictSlug } from '@/helper/linkCitySlug';
import showToast from '../toast/Toast';
import { makeRequest } from '@/services/makeRequest';
import {
	getFilterPopupStatus,
	setFilterMiniPopupStatus,
	setFilterPopupStatus,
} from '@/stores/slices/filterPopupSlice';
import {
	getIsMainPage,
	resetPage,
	setPage,
	setSearchValue,
} from '@/stores/slices/modelsState';
import { hide } from '@/stores/slices/showFilterSlice';
import { clearTitle } from '@/stores/slices/titleSlice';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const useFilterLogic = () => {
	// Композиция маленьких хуков
	const { lang, t, city, scrollToTop, country } = useLadyService();

	const computed = useFilterComputed();
	const url = useUrlParams();
	const profiles = useProfilesFetching();
	const sync = useFilterStateSync();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams();
	const isMainPage = useSelector(getIsMainPage);

	const location = useLocation();

	//  Redux селекторы
	const filterState = useSelector(getFilterState);
	const filterQuery = useSelector(getFilterQuery);
	const isFilterEdit = useSelector(getFilterEditObj);
	const countModelsOnMap = useSelector(getProfilesOnMapCount);
	const isStatusFullPopup = useSelector(getFilterPopupStatus);
	const current_city = useSelector(getUserCurrentCity);
	const current_dist = useSelector(getUserCurrentDistrict);
	const countriesData = useSelector(getCountriesData);

	const [isActive, setIsActive] = useState(false);

	const [editFilterName, setEditFilterName] = useState(
		isFilterEdit ? isFilterEdit.title : '',
	);

	// оригинальные функции
	const queryStringToObject = useCallback(
		(queryString) => {
			const params = queryString?.split('&');
			const result = {
				originalStr: queryString,
			};

			const { status } = filterState;

			params?.forEach((param) => {
				const [key, value] = param.split('=');
				result[key] = value;
			});
			if (status.length) {
				return { ...result, status: status.join(',') };
			} else {
				return result;
			}
		},
		[filterState],
	);

	const handleEditFilter = useCallback(
		async (id, title = 'FILTER: ' + id, filterState) => {
			try {
				const route = `user/filter/${id}`;
				const method = 'POST';
				const payload = {
					title: title,
					filter: filterState.filter_query,
					filterState: { ...filterState, city },
				};

				const result = await makeRequest({ route, method, payload });
				if (result) {
					showToast({
						message: t('success'),
						variant: 'success',
					});
				}
			} catch (error) {
				console.error('error: ', error);

				showToast({
					message: t('oops'),
					subMessage: `unable to save filter: ${error.message}`,
					variant: 'error',
				});
			}
		},
		[city, t],
	);

	const handleLocationClick = useCallback(async () => {
		setIsActive(true);

		if (!('geolocation' in navigator)) {
			console.error('Geolocation is not supported by this browser.');
			return false;
		}

		try {
			// Ждем результат геолокации
			const position = await new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					enableHighAccuracy: true,
					timeout: 15000,
					maximumAge: 300000,
				});
			});

			const { latitude, longitude } = position.coords;

			const response = await axios.get(
				`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&accept-language=${lang}`,
			);

			const located = response.data.address;
			const { city } = located;

			// Проверка города
			let uncorrectName = false;
			country.map((item) => {
				if (
					item.title?.toLowerCase() !== city?.toLowerCase() &&
					!uncorrectName
				) {
					uncorrectName = true;
					dispatch(
						setCurrentCity(
							country.filter((item) => item.id === 1)[0],
						),
					);
					return null;
				}
			});

			// Возвращаем результат
			return { ...located, latitude, longitude };
		} catch (error) {
			console.error('Geolocation or API error:', error);
			return false;
		}
	}, [lang, country, dispatch]);

	const handleApplyFilter = useCallback(
		async (filterObj, setOpen, redirectAnyway = false) => {
			dispatch(setProfilesOnMapCount(countModelsOnMap));
			dispatch(setSearchValue(''));
			dispatch(hide());
			dispatch(updateFilter(filterObj));

			// Apply filter state to URL and trigger API call
			// This will set filterQuery which triggers useProfilesFetching
			url.applyFilterToUrl(filterObj, redirectAnyway);

			if (!isStatusFullPopup) {
				dispatch(setFilterMiniPopupStatus(true));
				dispatch(setFilterPopupStatus(true));
			}
			dispatch(setFilterMiniPopupStatus(true));

			if (setOpen) {
				setOpen(false);
			}
			if (!location.pathname.includes('/agency/')) {
				scrollToTop();
			}
		},
		[countModelsOnMap, url, dispatch, isStatusFullPopup, scrollToTop],
	);

	const handleInstantCheckBox = (e) => {
		const { name, value } = e.target;
		const payload = { key: name, payload: value };
		dispatch(resetPage());

		handleApplyFilter(
			syncronomousToggleSomeFilter(filterState, payload),
			false,
			true,
		);
	};

	const handleRangeChange = useCallback(
		(key, values) => {
			const payload = { key, values };

			dispatch(updateCurrentRanges(payload));

			// console.group('!!! INSTANT_UPDATE');
			// console.log('➡️  STATE USED TO UPDATE:', filterState);
			// console.log('➡️  PAYLOAD:', payload);
			// console.groupEnd();

			handleApplyFilter(
				syncronomousUpdateRangesFilter(filterState, payload),
				false,
				true,
			);
		},
		[dispatch, filterState, handleApplyFilter],
	);

	const handleInstantRadio = useCallback(
		(payload) => {
			//objects of type {gender: ['male']}
			// dispatch(updateFilter(payload));

			// console.trace('TRIGGER FILTER');

			// console.group('!!! INSTANT_UPDATE');
			// console.log('➡️  STATE USED TO UPDATE:', filterState);
			// console.log('➡️  PAYLOAD:', payload);
			// console.groupEnd();

			handleApplyFilter(
				syncronomousUpdateFilter(filterState, payload, true),
				false,
				true,
			);
		},
		[filterState, handleApplyFilter],
	);

	const resetFilter = useCallback(
		(toMain = false, path = false, countrySelect = null) => {
			// console.trace('RESET');
			if (toMain) {
				localStorage.removeItem('save_gender_filter');
			}

			dispatch(setSearchNick(null));
			dispatch(setSearchValue(null));
			dispatch(clearTitle());
			dispatch(resetPage());

			if (countrySelect) {
				console.log(`=> countrySelect: `, countrySelect);
				dispatch(
					setFilterQuery(
						filterState.filter_query.filter(
							(i) =>
								i.startsWith('place=') ||
								i.startsWith('gender=') ||
								i.startsWith('massage='),
						),
					),
				);
			} else {
				dispatch(updateFilter(null));
				dispatch(setFilterQuery([]));
			}

			if (toMain || params.uuid) {
				const preferredCountrySlug =
					typeof window !== 'undefined'
						? localStorage.getItem('preferredCountrySlug')
						: null;
				const fallbackCitySlug =
					params.city ||
					(current_city?.slug && current_city.slug !== 'czechia'
						? current_city.slug
						: preferredCountrySlug || current_city?.slug) ||
					'czechia';
				// console.trace(
				// 	location.pathname,
				// 	'RESET checking items: params - ',
				// 	params,
				// 	'current_city -',
				// 	current_city,
				// 	toMain && !path
				// 		? `=> goes to main: ` +
				// 				`/${lang}/${current_city?.slug ?? params.city ?? 'czechia'}`
				// 		: '',
				// 	path ? `=> goes to path: ` + path : '',
				// );

				if (!path && ((location.pathname.includes('/profile/') && !location.pathname.includes('/edit'))  || isMainPage)) {
					dispatch(
						setLinksCurrentCity(
							fallbackCitySlug,
						),
					);

					const currentCitySlug = fallbackCitySlug;
					const currentDistrictSlug = getValidDistrictSlug(
						currentCitySlug,
						current_dist,
						countriesData,
					);

					const cityRoute = currentCitySlug
						? currentDistrictSlug
							? `${currentCitySlug}_${currentDistrictSlug}`
							: `${currentCitySlug}`
						: undefined;

					const newUrl =
						lang === 'en' && currentCitySlug === 'czechia'
							? `/`
							: `/${lang}/${cityRoute ?? 'czechia'}`;

					navigate(newUrl);
				}

				// handleApplyFilter();
			}

			if (path) {
				navigate(path);
			}
		},
		[
			dispatch,
			current_city,
			current_dist,
			countriesData,
			handleApplyFilter,
			lang,
			params,
			location.pathname,
			navigate,
		],
	);

	useEffect(() => {
		const handlePopState = () => {
			// const storedPage = localStorage?.getItem('page');
			// const storedModel = localStorage?.getItem('last_model');
			// const el = document.getElementById(storedModel);
			// console.log(`=> popstate`, storedPage);
			// if (storedPage) {
			// 	console.log(`=> goto stored`);
			// 	dispatch(setPage(Number(storedPage)));
			// 	if (el)
			// 		try {
			// 			setTimeout(() => {
			// 				window?.scrollTo({
			// 					top: el.offsetTop,
			// 					behavior: 'smooth',
			// 				});
			// 			}, 300);
			// 		} catch (err) {
			// 			console.error(err);
			// 			console.log(storedModel);
			// 		}
			// }
			// localStorage?.removeItem('page');
			// localStorage?.removeItem('last_model');
			// resetFilter(false);
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, []);

	//  ВСЕ ВМЕСТЕ
	return {
		// Основное состояние
		filterState,
		isFilterEdit,
		countModelsOnMap,

		// Вычисляемые значения
		...computed,

		// URL параметры
		...url,

		// Профили и загрузка
		...profiles,

		// Локальное состояние синхронизации
		filtersInitialized: sync.filtersInitialized,
		editFilterName: sync.editFilterName,

		// Filter handlers
		handleInstantRadio,
		handleInstantCheckBox,
		handleEditFilter,
		handleLocationClick,
		handleApplyFilter,
		resetFilter,
		handleRangeChange,
	};
};

export default useFilterLogic;
